/** "Ask Dockie": your own thread over the team's records; read a teammate's to pick up from it. */
import React, { useEffect, useRef, useState } from "react";
import { ArrowUp, Eye } from "lucide-react";
import { useAskDockie, useDockieThread, useDockieThreads } from "@/services/python-api/customerHooks";
import { Avatar } from "./parts";

const STARTERS = ["What's waiting on us?", "Where is the GLE 53?", "Which papers do we have?"];

function Bubble({ m, who }) {
  if (m.author === "member") {
    return (
      <div className="flex justify-end">
        <p className="max-w-[80%] rounded-2xl rounded-br-md bg-[#2563EB] px-4 py-2.5 text-[15px] leading-relaxed text-white">
          {m.text}
        </p>
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#101928] text-[12px] font-bold text-white">
        D
      </span>
      <p className="max-w-[85%] whitespace-pre-line text-[15px] leading-relaxed text-[#1D2739]">{m.text}</p>
    </div>
  );
}

export default function MyDockie() {
  const { data: list } = useDockieThreads();
  const [whose, setWhose] = useState(null);
  const { data: thread } = useDockieThread(whose);
  const ask = useAskDockie();
  const [text, setText] = useState("");
  const end = useRef(null);
  const mine = !whose;
  const messages = thread?.messages ?? [];
  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);
  const send = (q) => {
    const said = (q ?? text).trim();
    if (!said) return;
    setText("");
    ask.mutate(said);
  };
  return (
    <div className="flex h-full min-h-0 flex-1">
      <aside className="hidden w-[260px] shrink-0 border-r border-[#F0F2F5] bg-[#FCFCFD] p-3 lg:block">
        <p className="px-2 pb-2 pt-1 text-[12px] font-semibold uppercase tracking-wide text-[#98A2B3]">Threads</p>
        {(list?.threads ?? []).map((t) => {
          const active = t.mine ? mine : whose === t.member_id;
          return (
            <button
              key={t.member_id}
              onClick={() => setWhose(t.mine ? null : t.member_id)}
              className={`mb-1 flex w-full items-start gap-2.5 rounded-xl px-2 py-2 text-left ${
                active ? "bg-[#EFF6FF]" : "hover:bg-[#F5F5F5]"
              }`}
            >
              <Avatar name={t.name} size={28} />
              <span className="min-w-0">
                <span className="block text-[14px] font-medium text-[#101928]">{t.mine ? "You" : t.name}</span>
                <span className="block truncate text-[12px] text-[#667185]">{t.last ?? "Ask anything"}</span>
              </span>
            </button>
          );
        })}
      </aside>
      <section className="flex min-h-0 flex-1 flex-col">
        {/* On a phone the threads are a row of people to tap. */}
        <div className="flex gap-2 overflow-x-auto border-b border-[#F0F2F5] px-4 py-2.5 lg:hidden">
          {(list?.threads ?? []).map((t) => {
            const active = t.mine ? mine : whose === t.member_id;
            return (
              <button
                key={t.member_id}
                onClick={() => setWhose(t.mine ? null : t.member_id)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[13px] ${
                  active ? "border-[#2563EB] bg-[#EFF6FF] text-[#1D4ED8]" : "border-[#E4E7EC] text-[#344054]"
                }`}
              >
                <Avatar name={t.name} size={20} /> {t.mine ? "You" : t.name}
              </button>
            );
          })}
        </div>
        {!mine ? (
          <div className="flex items-center gap-2 border-b border-[#F0F2F5] bg-[#F9FAFB] px-5 py-2.5 text-[13px] text-[#475367]">
            <Eye size={15} /> Reading {thread?.member?.name}'s thread. Pick up from here in your own.
            <button className="ml-auto font-medium text-[#2563EB]" onClick={() => setWhose(null)}>
              Back to yours
            </button>
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-[720px] space-y-6 px-5 py-8">
            {messages.length === 0 && mine ? (
              <div className="pt-10 text-center">
                <p className="text-[24px] font-semibold text-[#101928]">What can I find for you?</p>
                <p className="mt-2 text-[15px] text-[#667185]">
                  Your cars, papers and invoices, and where your teammates left off.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {STARTERS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="rounded-full border border-[#E4E7EC] px-4 py-2 text-[14px] text-[#344054] hover:bg-[#F9FAFB]"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {messages.map((m) => (
              <Bubble key={m.id} m={m} />
            ))}
            {ask.isPending ? <p className="pl-10 text-[14px] text-[#98A2B3]">Looking through your records…</p> : null}
            <div ref={end} />
          </div>
        </div>
        {mine ? (
          <div className="border-t border-[#F0F2F5] bg-white px-4 py-3">
            <div className="mx-auto flex max-w-[720px] items-end gap-2 rounded-3xl border border-[#D0D5DD] bg-white px-4 py-2 shadow-sm focus-within:border-[#2563EB]">
              <textarea
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask Dockie about any car, paper or invoice"
                className="max-h-40 flex-1 resize-none bg-transparent py-1.5 text-[15px] outline-none"
              />
              <button
                onClick={() => send()}
                disabled={!text.trim() || ask.isPending}
                className="mb-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-[#2563EB] disabled:bg-[#D0D5DD]"
                aria-label="Send"
              >
                <ArrowUp size={18} color="white" />
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
